import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import InstallIcon from './InstallIcon';
import { getInstallGuides } from './installGuides';

/**
 * The single primary action on the landing page: one device-aware install
 * button, a quiet "open in browser" fallback, and (in the hero) an
 * "Other devices" disclosure with steps for every platform.
 */
export const primaryLabelForMode = (mode, t) => {
  switch (mode) {
    case 'installed': return t('download.cta.open', 'Open On-Screen');
    case 'prompt': return t('download.cta.installFree', 'Install On-Screen, free');
    case 'inapp': return t('download.cta.inapp', 'How to install from here');
    case 'unsupported': return t('download.cta.openBrowser', 'Open On-Screen');
    default: return t('download.cta.install', 'Install On-Screen');
  }
};

export default function InstallCTA({ mode, onPrimary, onOpenBrowser, variant = 'hero', showOtherDevices = false }) {
  const { t } = useTranslation();
  const [othersOpen, setOthersOpen] = useState(false);
  const guides = getInstallGuides(t);

  const hint = {
    prompt: t('download.cta.hintPrompt', 'One tap. No app store, no account needed to install.'),
    ios: t('download.cta.hintIos', 'Two taps from Safari’s Share menu. We’ll show you.'),
    'android-manual': t('download.cta.hintAndroid', 'Installs from your browser menu. We’ll show you.'),
    'mac-safari': t('download.cta.hintMac', 'Add it to your Dock from Safari. We’ll show you.'),
    'desktop-manual': t('download.cta.hintDesktop', 'Installs from Chrome or Edge in one click.'),
    inapp: t('download.cta.hintInapp', 'You’re in an in-app browser. Open this page in Safari or Chrome to install.'),
    unsupported: t('download.cta.hintUnsupported', 'Your browser can’t install apps. Use it here, or install from Chrome, Edge, or Safari.'),
    installed: t('download.cta.hintInstalled', 'On-Screen is installed on this device.'),
  }[mode];

  return (
    <div className={`install-cta install-cta-${variant}`}>
      <div className="install-cta-row">
        <button type="button" className="cta-button primary large install-cta-primary" onClick={onPrimary}>
          <InstallIcon name={mode === 'installed' || mode === 'unsupported' ? 'arrowRight' : 'download'} />
          {primaryLabelForMode(mode, t)}
        </button>
        {mode !== 'installed' && mode !== 'unsupported' && (
          <button type="button" className="install-cta-browser" onClick={onOpenBrowser}>
            {t('download.cta.orBrowser', 'or open in your browser')}
            <InstallIcon name="arrowRight" />
          </button>
        )}
      </div>
      {hint && <p className="install-cta-hint">{hint}</p>}

      {showOtherDevices && (
        <div className="install-others">
          <button
            type="button"
            className="install-others-toggle"
            aria-expanded={othersOpen}
            aria-controls="install-others-panel"
            onClick={() => setOthersOpen((v) => !v)}
          >
            <InstallIcon name="devices" />
            {t('download.cta.otherDevices', 'Installing on another device?')}
            <InstallIcon name="chevronDown" className={`install-others-chevron${othersOpen ? ' open' : ''}`} />
          </button>
          <div id="install-others-panel" className="install-others-panel" hidden={!othersOpen}>
            {['ios', 'android', 'desktop', 'mac'].map((key) => (
              <div key={key} className="install-others-card">
                <h3>{guides[key].title}</h3>
                <ol>
                  {guides[key].steps.map((s) => <li key={s.text}>{s.text}</li>)}
                </ol>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
